<script setup lang="ts">
import { ref, type Ref } from "vue";
import { useI18n } from "vue-i18n";
import { VALIDATION_REGEXP } from "~/core/constants/constants";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useUpdateUser } from "~/modules/user/composables/useUpdateUser";
import type { UserDetailV2 } from "~/modules/user/models/UserDetailV2.interface";
import type { UserEmail } from "~/modules/user/models/UserEmail.interface";
import type { UserInformation } from "~/modules/user/models/UserInformation.interface";
import type { UserLanguage } from "~/modules/user/models/UserLanguage.interface";
import { UserSessionEntityType } from "~/modules/user/models/UserSessionEntityType.enum";
import type { UserUpdateDto } from "~/modules/user/models/UserUpdateDto.interface";
import { useUserSession } from "~/modules/user/stores/useUserSession";
import { getProfileLabel } from "~/modules/user/utils/profileLabel";
import { fallbackLocaleTag, setLocaleTag } from "~/plugins/i18n";
import ILxcMail from "~icons/lxc/mail";

const props = defineProps<{
  user?: UserDetailV2;
  useExternalIdp?: boolean;
}>();

const emits = defineEmits(["update:user"]);

const { userSession } = useUserSession();

const businessUnitCode = ref();
const organizationCode = ref();

switch (userSession?.entity.type) {
  case UserSessionEntityType.BUSINESS_UNIT:
    businessUnitCode.value = userSession.entity.code;
    break;
  case UserSessionEntityType.OPERATOR:
  case UserSessionEntityType.ADMINISTRATION:
    organizationCode.value = userSession.entity.code;
    break;
}

const { error: errorUpdateUser, updateUser } = useUpdateUser({
  organizationCode: organizationCode.value,
  businessUnitCode: businessUnitCode.value,
});

const isOperatorEntity = computed(
  () => userSession?.entity.type === UserSessionEntityType.OPERATOR,
);

const formInformationRef = ref();
const formEmailRef = ref();
const { t, te } = useI18n();
const isDisabledInformation = ref(true);
const isDisabledEmail = ref(true);

const newLanguage: Ref<UserLanguage | null> = ref(null);

defineExpose({ saveLanguage });

function setUserForm(user?: UserDetailV2) {
  setUserInformationForm(user);
  setUserEmailForm(user);
}

/**
 * User forms rules
 */
const userInformationFormValidationRules = reactive({
  firstName: [
    { required: true, message: t("input.error.required"), trigger: "blur" },
    { max: 80, message: t("input.error.maxLength", { maxLength: 80 }) },
  ],
  lastName: [
    { required: true, message: t("input.error.required"), trigger: "blur" },
    { max: 80, message: t("input.error.maxLength", { maxLength: 80 }) },
  ],
});

const userEmailFormValidationRules = reactive({
  email: [
    { required: true, message: t("input.error.required"), trigger: "blur" },
    { max: 255, message: t("input.error.maxLength", { maxLength: 255 }) },
    {
      pattern: VALIDATION_REGEXP.EMAIL,
      message: t("input.error.invalidFormat"),
      trigger: "blur",
    },
  ],
});

/**
 * User information properties
 */
const userInformationForm: UserInformation = reactive({
  login: "",
  organizationName: "",
  firstName: "",
  lastName: "",
  groups: "",
  sectors: "",
  profile: "",
});

function setUserInformationForm(user?: UserDetailV2) {
  Object.assign(userInformationForm, {
    login: user?.login,
    organizationName: userSession?.entity.name,
    firstName: user?.firstName,
    lastName: user?.lastName,
    groups: user?.groups?.map((group) => group.name).join(", "),
    sectors: userSession?.sectors?.map((sector) => sector.name).join(", "),
    profile: user ? getProfileLabel(user?.profile, t, te) : "",
  });
}

const isSavingUserInformation: Ref<boolean> = ref(false);

async function saveUserInformation() {
  isSavingUserInformation.value = true;

  if (await saveUser()) {
    isDisabledInformation.value = !isDisabledInformation.value;
  }

  isSavingUserInformation.value = false;
}

async function cancelUserInformation() {
  setUserInformationForm(props.user);
  isDisabledInformation.value = !isDisabledInformation.value;
}

/**
 * User email properties
 */
const userEmailForm: UserEmail = reactive({
  email: "",
});

function setUserEmailForm(user?: UserDetailV2) {
  Object.assign(userEmailForm, {
    email: user?.email,
  });
}

const isSavingUserEmail: Ref<boolean> = ref(false);

async function saveUserEmail() {
  isSavingUserEmail.value = true;

  if (await saveUser()) {
    isDisabledEmail.value = !isDisabledEmail.value;
  }

  isSavingUserEmail.value = false;
}

async function cancelUserEmail() {
  setUserEmailForm(props.user);
  isDisabledEmail.value = !isDisabledEmail.value;
}

/**
 * User language properties
 */
function saveLanguage(language: UserLanguage) {
  newLanguage.value = language;
  saveUser();
}

function getUserLocale(): string {
  const userLocale = newLanguage.value
    ? newLanguage.value.language
    : props.user?.language;
  return userLocale ?? fallbackLocaleTag;
}

/**
 * Save user
 */
async function saveUser(): Promise<boolean> {
  let succeed = false;

  const isFormInformationValid = await formInformationRef.value
    .validate()
    .catch(() => false);
  const isFormEmailValid = await formEmailRef.value
    .validate()
    .catch(() => false);

  if (props.user && isFormInformationValid && isFormEmailValid) {
    const userUpdatedAttributes = {
      firstName: userInformationForm.firstName,
      lastName: userInformationForm.lastName,
      email: userEmailForm.email,
      language: getUserLocale(),
    };

    const userUpdateDto: UserUpdateDto = {
      active: props.user.active,
      profile: props.user.profile.id,
      ...userUpdatedAttributes,
    };

    await updateUser(userInformationForm.login, userUpdateDto);

    if (errorUpdateUser.value) {
      errorUpdateUser.value.notify(NotificationKey.saveError);
    } else {
      // build a user with the updated information
      const user = {
        ...props.user,
        ...userUpdatedAttributes,
      };

      // update i18n locale
      setLocaleTag(user.language);

      // update the user session information on front side only
      if (userSession) {
        userSession.login = user.login;
        userSession.firstName = user.firstName;
        userSession.lastName = user.lastName;
        userSession.email = user.email;
        userSession.language = user.language;
      }

      emits("update:user", user);
      showNotificationSuccess(t(NotificationKey.saveSuccess));
      succeed = true;
    }
  }

  return succeed;
}

const classFormWrapper =
  "grid xl:grid-cols-4 lg:grid-cols-2 sm:grid-cols-1 gap-x-8";

watch(() => props.user, setUserForm);

onMounted(() => setUserForm(props.user));
</script>

<template>
  <container-component :px="0" :py="0">
    <h2>{{ t("user.userAccount.userInformations.title") }}</h2>
    <mandatory-component class="!mt-0" />
    <p class="text-gray-600">
      {{ t("user.userAccount.instructions.informations") }}
    </p>

    <!-- User information form -->
    <lxc-form
      ref="formInformationRef"
      :rules="userInformationFormValidationRules"
      :model="userInformationForm"
    >
      <div :class="classFormWrapper">
        <lxc-form-item :label="t('user.userAccount.forms.organisationName')">
          <lxc-input
            v-model="userInformationForm.organizationName"
            type="text"
            disabled
          />
        </lxc-form-item>
        <lxc-form-item
          v-if="isOperatorEntity"
          :label="t('user.form.userGroups.title')"
        >
          <lxc-input
            v-model="userInformationForm.groups"
            type="text"
            disabled
          />
        </lxc-form-item>
        <lxc-form-item
          v-if="isOperatorEntity"
          :label="t('user.userAccount.forms.sectors')"
        >
          <lxc-input
            v-model="userInformationForm.sectors"
            type="text"
            disabled
          />
        </lxc-form-item>
        <lxc-form-item :label="t('user.userAccount.forms.profile')">
          <lxc-input
            v-model="userInformationForm.profile"
            type="text"
            disabled
          />
        </lxc-form-item>
        <lxc-form-item
          class="xl:row-start-2"
          :label="t('user.userAccount.forms.login')"
          prop="login"
        >
          <lxc-input v-model="userInformationForm.login" type="text" disabled />
        </lxc-form-item>
        <lxc-form-item
          class="xl:row-start-2"
          :label="t('user.userAccount.forms.firstName')"
          prop="firstName"
        >
          <lxc-input
            v-model="userInformationForm.firstName"
            type="text"
            :disabled="isDisabledInformation"
          />
        </lxc-form-item>
        <lxc-form-item
          class="xl:row-start-2"
          :label="t('user.userAccount.forms.lastName')"
          prop="lastName"
        >
          <lxc-input
            v-model="userInformationForm.lastName"
            type="text"
            :disabled="isDisabledInformation"
          />
        </lxc-form-item>
      </div>
      <lxc-form-item v-if="!useExternalIdp">
        <lxc-button
          v-if="isDisabledInformation"
          type="secondary"
          :title="t('user.userAccount.forms.edit')"
          @click="isDisabledInformation = !isDisabledInformation"
        >
          {{ t("user.userAccount.forms.edit") }}
        </lxc-button>
        <div v-if="!isDisabledInformation" class="flex gap-2.5">
          <lxc-button
            :title="t('user.userAccount.forms.validate')"
            :is-loading="isSavingUserInformation"
            @click.prevent="saveUserInformation"
          >
            {{ t("user.userAccount.forms.validate") }}
          </lxc-button>
          <lxc-button
            :title="t('user.userAccount.forms.cancel')"
            type="secondary"
            @click="cancelUserInformation"
          >
            {{ t("user.userAccount.forms.cancel") }}
          </lxc-button>
        </div>
      </lxc-form-item>
    </lxc-form>

    <hr class="my-4" />

    <!-- User email form -->
    <lxc-form
      ref="formEmailRef"
      :rules="userEmailFormValidationRules"
      :model="userEmailForm"
    >
      <div :class="classFormWrapper">
        <lxc-form-item prop="email">
          <lxc-label required :disabled="isDisabledEmail">
            {{ t("user.userAccount.forms.email") }}
          </lxc-label>
          <p class="text-gray-600">
            {{ t("user.userAccount.instructions.email") }}
          </p>
          <lxc-input
            v-model="userEmailForm.email"
            type="email"
            :disabled="isDisabledEmail"
          >
            <template #prefix>
              <i-lxc-mail />
            </template>
          </lxc-input>
        </lxc-form-item>
      </div>
      <lxc-form-item v-if="!useExternalIdp">
        <lxc-button
          v-if="isDisabledEmail"
          type="secondary"
          :title="t('user.userAccount.forms.edit')"
          @click="isDisabledEmail = !isDisabledEmail"
        >
          {{ t("user.userAccount.forms.edit") }}
        </lxc-button>
        <div v-if="!isDisabledEmail" class="flex gap-2.5">
          <lxc-button
            :title="t('user.userAccount.forms.validate')"
            :is-loading="isSavingUserEmail"
            @click.prevent="saveUserEmail"
          >
            {{ t("user.userAccount.forms.validate") }}
          </lxc-button>
          <lxc-button
            :title="t('user.userAccount.forms.cancel')"
            type="secondary"
            @click="cancelUserEmail"
          >
            {{ t("user.userAccount.forms.cancel") }}
          </lxc-button>
        </div>
      </lxc-form-item>
    </lxc-form>
  </container-component>
</template>
